import { OptionsApi, VideoPublisherStatus } from '@core/swagger-client';
import { ChatChannelIds, ClientType } from '@core/types';
import {
    Button,
    CompositePageHeader,
    ContainerNoTopSpace,
    ContainerWebinarInner,
    ContentHeader,
    Download,
    ErrorMessage,
    Heading,
    Loader,
    PageHeader,
    PageHeaderContent
} from '@core/ui';
import { useParams } from '@online-webinar/router';
import { onDemandCurrentVideoTimeSeeded, onDemandJoinWebinar, onDemandLeaveWebinar, toTimespan, useAppDispatch, useAppSelector } from '@online-webinar/store';
import { BackToOriginLink } from '@online-webinar/ui-frontend';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Duration } from 'luxon';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    alignDurationToKeyframe,
    alignToKeyframe,
    BaseTimelineEvent,
    ChapterTimelineEvent,
    ChatTimelineEvent,
    CutTimelineEvent,
    parseOnDemandEvents,
    parseTimeline,
    PollTimelineEvent,
    SlideTimelineEvent,
    Timeline
} from '../../../parser';
import { ActiveTrack } from '../../atoms';
import { ChapterEditorOrganism } from '../../organism/ChapterEditorOrganism';
import { ChatEditorOrganism } from '../../organism/ChatEditorOrganism';
import { PollEditorOrganism } from '../../organism/PollEditorOrganism';
import { PublishingDialogOrganism } from '../../organism/PublishingDialogOrganism';
import { SlideEditorOrganism } from '../../organism/SlideEditorOrganism';
import { TimelineOrganism } from '../../organism/TimelineOrganism';
import { TrimVideoEditorOrganism } from '../../organism/TrimVideoEditorOrganism';
import { UploadVideoOrganism } from '../../organism/UploadVideoOrganism';
import { VideoLayoutEditorOrganism } from '../../organism/VideoLayoutEditorOrganism';

export const VideoEditorPage = () => {
    const { webinarId } = useParams({ strict: false });
    const { t } = useTranslation();
    const { data: webinar } = useQuery(OptionsApi.webinar.get(webinarId!, { staleTime: 60_000 }));
    const dispatch = useAppDispatch();

    const [showUploadVideo, setShowUploadVideo] = useState(false);
    const [isPublishing, setIsPublishing] = useState(false);
    const [timeline, setTimeline] = useState<Timeline>();
    const [duration, setDuration] = useState(Duration.fromObject({ seconds: 0 }));
    const [currentTime, setCurrentTime] = useState(Duration.fromObject({ seconds: 0 }));
    const [activeTrack, setActiveTrack] = useState<ActiveTrack>(ActiveTrack.Slide);

    const publishStatus = useQuery(OptionsApi.videoEditor.getStatus(webinarId!, { refetchInterval: 5000 }));
    const keyFrames = useQuery(
        OptionsApi.videoEditor.getKeyframes(webinarId!, {
            retry: () => true,
            retryDelay: 1000
        })
    );

    const videoRef = useRef<HTMLVideoElement>(null);
    useEffect(() => {
        dispatch(onDemandJoinWebinar({ webinarId: webinarId!, clientType: ClientType.editor }));
        return () => {
            dispatch(onDemandLeaveWebinar());
        };
    }, []);

    useEffect(() => {
        if (!keyFrames.data || !timeline) {
            return;
        }

        timeline.keyframes = keyFrames.data.map((f) => Duration.fromISOTime(f));
    }, [keyFrames.data, timeline]);

    useEffect(() => {
        if (!duration || !timeline) {
            return;
        }

        timeline.duration = duration;
    }, [duration, timeline]);

    // Seeded, not Updated: this only needs to drive local UI (activeChapter/activeSlideNumber for the
    // editor's own preview panes, derived from the same applyCurrentVideoTime reducer either action
    // uses) — it must never be forwarded to the server as real on-demand playback. Updated is what the
    // on-demand saga's takeEvery watches to report a viewing session; scrubbing through the timeline
    // while editing is not the owner actually watching the webinar, and was silently becoming their own
    // future resume position for it.
    useEffect(() => {
        dispatch(onDemandCurrentVideoTimeSeeded(Math.round(currentTime.as('seconds'))));
    }, [currentTime]);

    const isInitialized = useAppSelector((state) => state.onDemandWebinar.isInitialized);
    const onDemandData = useAppSelector((state) => state.onDemandWebinar.onDemandData);
    const onDemandEvents = useAppSelector((state) => state.onDemandWebinar.timeline);

    useEffect(() => {
        if (!onDemandEvents) {
            setTimeline(undefined);
            return;
        }

        setTimeline(parseOnDemandEvents(onDemandEvents));
    }, [onDemandEvents]);

    const onSeek = (seek: Duration) => {
        if (videoRef.current) {
            videoRef.current.currentTime = Math.round(seek.as('seconds'));
        }
    };

    const onSelectTrack = (track: ActiveTrack, event?: BaseTimelineEvent) => {
        setActiveTrack(track);
        if (event) {
            if (videoRef.current) {
                videoRef.current.currentTime = Math.round(event.timestamp.as('seconds'));
            }
        }
    };

    const onAdd = (track: ActiveTrack) => {
        if (!timeline) {
            return;
        }

        setActiveTrack(track);

        let align = false;
        let events: BaseTimelineEvent[] = [];
        let event: BaseTimelineEvent | null = null;
        switch (track) {
            case ActiveTrack.Slide:
                events = timeline.slides;
                event = { timestamp: currentTime, url: '../images/video-background.webp' } as SlideTimelineEvent;
                break;
            case ActiveTrack.Chapter:
                events = timeline.chapters;
                event = { timestamp: currentTime, title: t('Editor.Chapter.New') } as ChapterTimelineEvent;
                break;
            case ActiveTrack.Chat:
                events = timeline.chat;
                event = {
                    timestamp: currentTime,
                    message: {
                        channelId: ChatChannelIds.publicChannel,
                        messageId: crypto.randomUUID(),
                        message: t('Editor.Chat.New'),
                        senderClientType: ClientType.presenter,
                        senderParticipantId: '00000000-0000-0000-0000-000000000000',
                        senderNickname: 'Presenter',
                        timestamp: toTimespan(currentTime)
                    }
                } as ChatTimelineEvent;
                break;
            case ActiveTrack.VideoTrim:
                events = timeline.cuts;

                align = true;
                let timestamp = alignToKeyframe(timeline, currentTime);
                let desiredDuration = Duration.fromObject({ seconds: 60 });
                let duration = alignDurationToKeyframe(timeline, timestamp, desiredDuration);
                event = { timestamp, duration, desiredDuration } as CutTimelineEvent;

                break;
            default:
                break;
        }

        if (!event) {
            return;
        }

        let time = currentTime;
        while (!!events.find((c) => c.timestamp.equals(align ? alignToKeyframe(timeline, time) : time))) {
            time = time.plus({ seconds: 1 });
        }
        event.changed = true;
        event.timestamp = align ? alignToKeyframe(timeline, time) : time;

        // TODO: If we have a duration for the event, we should also check for overlapping events
        if (align && 'duration' in event) {
            event.duration = alignDurationToKeyframe(timeline, event.timestamp, event.duration as Duration);
        }

        if (videoRef.current) {
            videoRef.current.currentTime = Math.round(time.as('seconds'));
        }

        events.push(event);
        events = [...events.sort((a, b) => a.timestamp.toMillis() - b.timestamp.toMillis())];

        switch (track) {
            case ActiveTrack.Slide:
                timeline.slides = events as SlideTimelineEvent[];
                break;
            case ActiveTrack.Chapter:
                timeline.chapters = events as ChapterTimelineEvent[];
                break;
            case ActiveTrack.Chat:
                timeline.chat = events as ChatTimelineEvent[];
                break;
            case ActiveTrack.VideoTrim:
                timeline.cuts = events as CutTimelineEvent[];
                break;
            default:
                break;
        }
        timeline.changed = true;
    };

    const onDeletePoll = (poll: PollTimelineEvent) => {
        setTimeline((currentTimeline) => {
            if (!currentTimeline || !currentTimeline.polls.includes(poll)) {
                return currentTimeline;
            }

            return {
                ...currentTimeline,
                changed: true,
                polls: currentTimeline.polls.filter((currentPoll) => currentPoll !== poll)
            };
        });
    };

    const publishWebinar = useMutation(OptionsApi.videoEditor.publishWebinar());

    useEffect(() => {
        if (publishStatus.data?.status !== VideoPublisherStatus.Publishing) {
            return;
        }

        setIsPublishing(true);
    }, [publishStatus]);

    const saveTimeline = () => {
        if (!timeline) {
            return;
        }

        setIsPublishing(true);

        const events = parseTimeline(timeline);
        publishWebinar.mutate(
            { webinarId: webinarId!, events },
            {
                onError: () => {
                    setIsPublishing(false);
                },
                onSuccess: () => {
                    publishStatus.refetch().then(() => {});

                    timeline.changed = false;
                    timeline.chapters.forEach((chapter) => (chapter.changed = false));
                    timeline.chat.forEach((chat) => (chat.changed = false));
                    timeline.cuts.forEach((cut) => (cut.changed = false));
                    timeline.polls.forEach((poll) => (poll.changed = false));
                    timeline.slides.forEach((slide) => (slide.changed = false));
                }
            }
        );
    };

    if (!isInitialized || !onDemandData?.videoUrl || !timeline) {
        return <Loader classNames="m-auto" />;
    }

    return (
        <ContainerNoTopSpace>
            <PageHeader>
                <PageHeaderContent>{webinar && <CompositePageHeader title={webinar.title} broadcastDateTime={webinar.broadcastDateTime} />}</PageHeaderContent>
            </PageHeader>
            <ContainerWebinarInner>
                <BackToOriginLink fallback="content" />
                <ContentHeader contentHeader={t('Editor.PageHeading')} testId="editor" />
                {isPublishing && publishStatus.data && (
                    <PublishingDialogOrganism onClose={() => setIsPublishing(false)} open={true} state={publishStatus.data} />
                )}
                {showUploadVideo && <UploadVideoOrganism onClose={() => setShowUploadVideo(false)} />}
                <div className="editor bg-white grid grid-cols-2 gap-4 rounded-lg p-2">
                    <video
                        id="video"
                        ref={videoRef}
                        src={onDemandData.videoUrl.replace('m3u8', 'mp4') /** Replace m3u8 with mp4 so we have the original uncut video */}
                        onLoadedMetadata={(e) => {
                            setDuration(Duration.fromObject({ seconds: Math.round(e.currentTarget.duration) }));
                        }}
                        onTimeUpdate={(e) => {
                            setCurrentTime(Duration.fromObject({ seconds: Math.round(e.currentTarget.currentTime) }));
                        }}
                        className="w-full self-end"
                        controls
                    ></video>
                    {activeTrack === ActiveTrack.VideoTrim && <TrimVideoEditorOrganism timeline={timeline} currentTime={currentTime} onSeek={onSeek} />}
                    {activeTrack === ActiveTrack.Slide && <SlideEditorOrganism timeline={timeline} currentTime={currentTime} onSeek={onSeek} />}
                    {activeTrack === ActiveTrack.Chapter && <ChapterEditorOrganism timeline={timeline} currentTime={currentTime} onSeek={onSeek} />}
                    {activeTrack === ActiveTrack.Poll && (
                        <PollEditorOrganism timeline={timeline} currentTime={currentTime} onSeek={onSeek} onDelete={onDeletePoll} />
                    )}
                    {activeTrack === ActiveTrack.Chat && <ChatEditorOrganism timeline={timeline} currentTime={currentTime} onSeek={onSeek} />}
                    {activeTrack === ActiveTrack.VideoLayout && <VideoLayoutEditorOrganism timeline={timeline} currentTime={currentTime} onSeek={onSeek} />}
                    <TimelineOrganism
                        timeline={timeline}
                        currentTime={currentTime}
                        duration={duration}
                        activeTrack={activeTrack}
                        onSeek={onSeek}
                        onAdd={onAdd}
                        onSelectTrack={onSelectTrack}
                    />
                    <div>
                        <Button type="button" variant="primary-outline" onClick={() => setShowUploadVideo(true)}>
                            {t('Editor.Upload.Button')}
                        </Button>
                    </div>
                </div>
                <div className="flex flex-col sm:flex-row items-baseline gap-4 pt-4 pb-12">
                    <Button variant="primary" onClick={saveTimeline} classNames="flex-none" isLoading={publishWebinar.isPending}>
                        {t('Editor.Button.SaveOndemand')}
                    </Button>
                    {timeline.changed && <p>{t('Editor.SaveInfoText')}</p>}
                    {publishWebinar.isError && <ErrorMessage apiError={publishWebinar.error}></ErrorMessage>}
                    {!keyFrames.isSuccess && <small>{t('Editor.VideoBeingAnalyzed')}</small>}
                </div>
                <div className="max-w-4xl">
                    <Heading variant="h2" classNames="mb-4 lg:mb-6">
                        {t('Editor.Download.Video.Heading')}
                    </Heading>
                    <p className="mb-4 lg:mb-6">{t('Editor.Download.Video.Text')}</p>
                    <a className="btn btn--outline-primary" href={onDemandData?.videoUrl.replace('m3u8', 'mp4')} download>
                        <Download classNames="size-6 flex-none stroke-current menu:me-2" />
                        {t('Editor.Download.Video.Button')}
                    </a>
                </div>
            </ContainerWebinarInner>
        </ContainerNoTopSpace>
    );
};
